import React, {
	useState,
	useRef,
	useEffect,
	useMemo,
	useCallback,
} from "react";
import { gsap } from "gsap";

import "./Roadmap.scss";
import RoadmapCard from "../../components/Home/RoadmapCard";
import ImgJaggedLine from "../../assets/images/jagged-line.svg";
import staticRoadmapData from "./RoadmapData";

const Roadmap = React.forwardRef(
	({ isLocked, setLockScrollUp, setLockScrollDown }, ref) => {
		// Enhanced roadmap data with refs
		const roadmapData = useMemo(
			() =>
				staticRoadmapData.map((item) => ({
					...item,
					ref: React.createRef(),
				})),
			[]
		);

		// State and refs
		const [activeIndex, setActiveIndex] = useState(0);
		const isThrottled = useRef(false);
		const touchStartPos = useRef({ x: null, y: null });

		// Calculate z-index for stacking order
		const getZIndex = (index) => {
			return index <= activeIndex + 1
				? roadmapData.length
				: roadmapData.length - index;
		};

		// New handler for direct click navigation with a custom animation
		const goToPhase = useCallback(
			(index) => {
				if (index === activeIndex || isThrottled.current) return;

				isThrottled.current = true;

				const currentCard = roadmapData[activeIndex].ref.current;
				const nextCard = roadmapData[index].ref.current;

				const tl = gsap.timeline({
					onComplete: () => {
						// After animation, re-enable scroll based on the new active index
						if (index === 0) {
							setLockScrollUp(false);
							setLockScrollDown(true);
						} else if (index === roadmapData.length - 1) {
							setLockScrollUp(true);
							setLockScrollDown(false);
						} else {
							setLockScrollUp(true);
							setLockScrollDown(true);
						}
						isThrottled.current = false;
					},
				});

				// Animate current card out
				tl.to(currentCard, {
					y: activeIndex < index ? -55 : 55, // Move Up or Down
					duration: 0.3,
					ease: "power1.out",
				})
					.to(
						nextCard,
						{
							y: activeIndex < index ? 55 : -55, // Move Down or Up
							duration: 0.3,
							ease: "power1.out",
							onComplete: () => {
								setActiveIndex(index);
							},
						},
						"<"
					)
					.to(nextCard, {
						y: 0,
						duration: 0.3,
						ease: "power1.out",
					})
					.to(
						currentCard,
						{
							y: 0,
							duration: 0.3,
							ease: "power1.out",
						},
						"<"
					);
			},
			[activeIndex, roadmapData, setLockScrollDown, setLockScrollUp]
		);

		useEffect(() => {
			const activatePhase = (index) => {
				if (index < 0 || index >= roadmapData.length) {
					return;
				}

				const currentCard = roadmapData[activeIndex].ref.current;
				const nextCard = roadmapData[index].ref.current;

				const tl = gsap.timeline({
					onComplete: () => {
						// After animation, re-enable scroll based on the new active index
						if (index === 0) {
							setLockScrollUp(false);
							setLockScrollDown(true);
						} else if (index === roadmapData.length - 1) {
							setLockScrollUp(true);
							setLockScrollDown(false);
						} else {
							setLockScrollUp(true);
							setLockScrollDown(true);
						}
					},
				});

				// Animate current card out
				tl.to(currentCard, {
					y: activeIndex < index ? -55 : 55, // Move Up or Down
					duration: 0.3,
					ease: "power1.out",
				})
					.to(
						nextCard,
						{
							y: activeIndex < index ? 55 : -55, // Move Down or Up
							duration: 0.3,
							ease: "power1.out",
							onComplete: () => {
								setActiveIndex(index);
							},
						},
						"<"
					)
					.to(nextCard, {
						y: 0,
						duration: 0.3,
						ease: "power1.out",
					})
					.to(
						currentCard,
						{
							y: 0,
							duration: 0.3,
							ease: "power1.out",
						},
						"<"
					);
			};

			const handleWheel = (e) => {
				// Check if the scroll is primarily horizontal.
				const isHorizontalScroll =
					Math.abs(e.deltaX) + 2 > Math.abs(e.deltaY); // This handles horizontal trackpad scrolling. (+2 makes it to be more sensitive to horizontal scroll)
				if (e.shiftKey || isHorizontalScroll) {
					// It's horizontal, do nothing and let the browser handle it.
					return;
				}

				// Throttle to prevent animation overlap
				if (isThrottled.current) {
					return;
				}

				isThrottled.current = true;
				setTimeout(() => {
					isThrottled.current = false;
				}, 800);

				if (!isLocked) {
					return;
				}

				const isScrollingDown = e.deltaY > 0;

				if (isScrollingDown) {
					activatePhase(activeIndex + 1);
				} else {
					activatePhase(activeIndex - 1);
				}
			};

			const handleTouchStart = (e) => {
				touchStartPos.current = {
					x: e.touches[0].clientX,
					y: e.touches[0].clientY,
				};
			};

			const handleTouchEnd = (e) => {
				if (
					touchStartPos.current.y === null ||
					touchStartPos.current.x === null
				) {
					return;
				}

				if (isThrottled.current || !isLocked) {
					return;
				}

				const touchEndX = e.changedTouches[0].clientX;
				const touchEndY = e.changedTouches[0].clientY;
				const deltaX = touchStartPos.current.x - touchEndX;
				const deltaY = touchStartPos.current.y - touchEndY;

				// Reset touch start position for the next swipe
				touchStartPos.current = { x: null, y: null };

				// Ignore horizontal swipes
				if (Math.abs(deltaX) > Math.abs(deltaY)) {
					return;
				}

				const swipeThreshold = 50; // Min distance for a swipe gesture

				if (Math.abs(deltaY) > swipeThreshold) {
					isThrottled.current = true;
					setTimeout(() => {
						isThrottled.current = false;
					}, 800);

					if (deltaY > 0) {
						// Swiped up
						activatePhase(activeIndex + 1);
					} else {
						// Swiped down
						activatePhase(activeIndex - 1);
					}
				}
			};

			window.addEventListener("wheel", handleWheel);
			window.addEventListener("touchstart", handleTouchStart, {
				passive: true,
			});
			window.addEventListener("touchend", handleTouchEnd, {
				passive: true,
			});

			return () => {
				window.removeEventListener("wheel", handleWheel);
				window.removeEventListener("touchstart", handleTouchStart);
				window.removeEventListener("touchend", handleTouchEnd);
			};
		}, [
			activeIndex,
			isLocked,
			setLockScrollUp,
			setLockScrollDown,
			roadmapData,
		]);

		return (
			<section id="roadmap" className="roadmap" ref={ref}>
				<div className="roadmap-content-wrapper content-wrapper-lg">
					<div className="title-wrapper">
						<h1 className="roadmap-title">RoadMap</h1>
						<img
							className="jagged-line"
							src={ImgJaggedLine}
							alt="Jagged Line"
						/>
					</div>

					<div className="roadmap-layout">
						<div className="roadmap-indicators">
							{roadmapData.map((data, index) => (
								<button
									key={data.phase}
									className={`roadmap-indicator ${
										activeIndex === index ? "active" : ""
									}`}
									onClick={() => {
										goToPhase(index); // Use the new click handler
									}}
								>
									+
								</button>
							))}
						</div>

						<div className="roadmap-cards">
							{roadmapData.map((data, index) => (
								<RoadmapCard
									ref={data.ref}
									key={data.phase}
									phase={data.phase}
									items={data.items}
									isActive={activeIndex === index}
									style={{
										"--z": getZIndex(index),
										"--index": index,
									}}
									onClick={() => {
										goToPhase(index); // Use the new click handler
									}}
								/>
							))}
						</div>
					</div>
				</div>
			</section>
		);
	}
);

export default Roadmap;
